import { Routes, Route, Link, NavLink, Navigate, useLocation } from 'react-router-dom';
import { AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import HomePage from './pages/HomePage';
import OnViewPage from './pages/OnViewPage';
import VenuesPublicPage from './pages/VenuesPublicPage';
import VenueDetailPage from './pages/VenueDetailPage';
import AdminQueuePage from './pages/admin/AdminQueuePage';
import AdminEventsPage from './pages/admin/AdminEventsPage';
import AdminIngestionsPage from './pages/admin/AdminIngestionsPage';
import AdminSubmissionsPage from './pages/admin/AdminSubmissionsPage';
import AdminSourcesPage from './pages/admin/AdminSourcesPage';
import AdminVenuesPage from './pages/admin/AdminVenuesPage';
import AdminPendingVenuesPage from './pages/admin/AdminPendingVenuesPage';
import AdminSeriesPage from './pages/admin/AdminSeriesPage';
import AdminSubmitPage from './pages/admin/AdminSubmitPage';
import AdminChangeRequestPage from './pages/admin/AdminChangeRequestPage';
import AdminSettingsPage from './pages/admin/AdminSettingsPage';
import AdminNotificationsPage from './pages/admin/AdminNotificationsPage';
import AdminIdentifyHistoryPage from './pages/admin/AdminIdentifyHistoryPage';
import AdminRecalculationsPage from './pages/admin/AdminRecalculationsPage';
import AdminBulkRecalcPage from './pages/admin/AdminBulkRecalcPage';
import AdminReportsPage from './pages/admin/AdminReportsPage';
import AdminJobsPage from './pages/admin/AdminJobsPage';
import AdminScansPage from './pages/admin/AdminScansPage';
import AdminScanCadencePage from './pages/admin/AdminScanCadencePage';
import AdminAiTrainingPage from './pages/admin/AdminAiTrainingPage';
import AdminCoveragePage from './pages/admin/AdminCoveragePage';
import AdminAiExtractionPage from './pages/admin/AdminAiExtractionPage';
import AdminSecretsPage from './pages/admin/AdminSecretsPage';
import AdminExtensionKeysPage from './pages/admin/AdminExtensionKeysPage';
import VenueMapPage from './pages/VenueMapPage';
import EventsMapPage from './pages/EventsMapPage';
import CalendarPostsPage from './pages/CalendarPostsPage';
import MyTastePage from './pages/MyTastePage';
import MySubmissionsPage from './pages/MySubmissionsPage';
import SeriesPublicPage from './pages/SeriesPublicPage';
import SeriesDetailPage from './pages/SeriesDetailPage';
import SeriesMapPage from './pages/SeriesMapPage';
import NotificationsPage from './pages/NotificationsPage';
import EmailEventPage from './pages/EmailEventPage';
import { RequireAuth, RequireAdmin, useIsAdmin, SignInButton, SignOutButton } from './lib/auth';
import ThemeToggle from './components/ThemeToggle';

/**
 * Section dropdown for the top navbar. The trigger button highlights whenever
 * the current URL is inside the section (`prefix`), so e.g. Events ▾ stays
 * "active" while the user is on /events/map.
 */
interface NavDropdownItem {
  to: string;
  label: string;
  end?: boolean;
  /** When set, render a non-interactive `<h6>` group header before this item. */
  groupStart?: string;
  /** When true, render a divider line before this item. */
  dividerBefore?: boolean;
  /** bootstrap-icons class name (without the `bi bi-` prefix). */
  icon?: string;
  /** When true, only rendered for platform admins (hidden from regular users). */
  adminOnly?: boolean;
}

function SectionDropdown({
  prefix,
  label,
  icon,
  items,
}: {
  prefix: string;
  label: string;
  icon?: string;
  items: NavDropdownItem[];
}) {
  const isAdmin = useIsAdmin();
  const visible = items.filter((item) => !item.adminOnly || isAdmin);
  return (
    <li className="nav-item dropdown">
      {/*
        Render the top-level toggle as a real <a href> (via NavLink) so all
        click modes work naturally:
          • left-click  → navigate to the section landing page
          • middle-click → open in new tab
          • ctrl/right-click → native browser menu
        The dropdown menu opens on hover at ≥1lg via the CSS rule in
        `12-modernization.css`; on collapsed mobile the dropdown contents are
        rendered inline (same file) so users always have access without a
        click-toggle. We intentionally do NOT use Bootstrap's
        `data-bs-toggle="dropdown"` here — it would swallow left-click.
      */}
      <NavLink
        to={prefix}
        className={({ isActive }) => `nav-link dropdown-toggle${isActive ? ' active' : ''}`}
        aria-haspopup="true"
      >
        {icon && <i className={`bi bi-${icon} me-1`} aria-hidden="true" />}
        {label}
      </NavLink>
      <ul className="dropdown-menu shadow-sm">
        {visible.map((item, idx) => (
          <DropdownItem key={`${item.to}-${idx}`} item={item} />
        ))}
      </ul>
    </li>
  );
}

function DropdownItem({ item }: { item: NavDropdownItem }) {
  return (
    <>
      {item.dividerBefore && (
        <li>
          <hr className="dropdown-divider" />
        </li>
      )}
      {item.groupStart && (
        <li>
          <h6 className="dropdown-header">{item.groupStart}</h6>
        </li>
      )}
      <li>
        <NavLink
          to={item.to}
          end={item.end}
          className={({ isActive }) => `dropdown-item${isActive ? ' active' : ''}`}
        >
          {item.icon && <i className={`bi bi-${item.icon} me-2 text-body-secondary`} aria-hidden="true" />}
          {item.label}
        </NavLink>
      </li>
    </>
  );
}

const EVENTS_NAV: NavDropdownItem[] = [
  { to: '/events', label: 'List', icon: 'list-ul', end: true },
  { to: '/events/calendar', label: 'Calendar', icon: 'calendar3' },
  { to: '/events/map', label: 'Map', icon: 'map' },
  { to: '/events/my-taste', label: 'My taste', icon: 'heart' },
  { to: '/events/calendar-posts', label: 'Calendar posts', icon: 'images' },
  { to: '/events/on-view', label: 'On view', icon: 'easel' },
  { to: '/events/queue', label: 'Events Review Queue', icon: 'inboxes', dividerBefore: true, groupStart: 'Admin', adminOnly: true },
  { to: '/events/manage', label: 'Manage events', icon: 'pencil-square', adminOnly: true },
];

const VENUES_NAV: NavDropdownItem[] = [
  { to: '/venues', label: 'List', icon: 'list-ul', end: true },
  { to: '/venues/map', label: 'Map', icon: 'map' },
  { to: '/venues/manage', label: 'Manage venues', icon: 'pencil-square', dividerBefore: true, groupStart: 'Admin', adminOnly: true },
  { to: '/venues/pending', label: 'Pending venues', icon: 'hourglass-split', adminOnly: true },
];

const SERIES_NAV: NavDropdownItem[] = [
  { to: '/series', label: 'List', icon: 'list-ul', end: true },
  { to: '/series/map', label: 'Map', icon: 'map' },
  { to: '/series/manage', label: 'Manage series', icon: 'pencil-square', dividerBefore: true, groupStart: 'Admin', adminOnly: true },
];

const SUBMIT_NAV: NavDropdownItem[] = [
  { to: '/submit', label: 'Submit an event', icon: 'plus-circle', end: true },
  { to: '/submit/mine', label: 'My submissions', icon: 'card-checklist' },
  { to: '/submit/history', label: 'Submissions history', icon: 'clock-history', dividerBefore: true, groupStart: 'Admin', adminOnly: true },
  { to: '/submit/request-change', label: 'Request a change', icon: 'pencil', adminOnly: true },
];

/** Notifications has no sub-pages, so it stays a single nav link. */
function TopNavLink({ to, label, prefix, icon }: { to: string; label: string; prefix?: string; icon?: string }) {
  const { pathname } = useLocation();
  const matchPrefix = prefix ?? to;
  const isActive = pathname === matchPrefix || pathname.startsWith(`${matchPrefix}/`);
  return (
    <NavLink
      to={to}
      className={`nav-link${isActive ? ' active' : ''}`}
      aria-current={isActive ? 'page' : undefined}
    >
      {icon && <i className={`bi bi-${icon} me-1`} aria-hidden="true" />}
      {label}
    </NavLink>
  );
}

export default function App() {
  const isAdmin = useIsAdmin();
  return (
    <>
      <a href="#main" className="skip-link btn btn-primary">Skip to main content</a>
      {/*
        Wrap the entire chrome (navbar + main) in RequireAuth so anonymous
        visitors see ONLY a clean "Redirecting to sign-in…" splash — no
        navbar, no footer, no flash of menu links. RequireAuth kicks off
        an interactive Entra redirect for unauthed users, so this both
        improves UX and makes the SPA visibly private. Real security still
        sits at the Function App (Easy Auth v2 Return401 on every /api/*
        path except a handful of webhook routes — see infra/main.bicep).
      */}
      <RequireAuth>
      <header>
        <nav className="navbar navbar-expand-lg navbar-dark bg-dark" aria-label="Primary">
          <div className="container">
            <Link to="/" className="navbar-brand d-inline-flex align-items-center">
              <img src="/favicon.svg" alt="" width="28" height="28" className="me-2" />
              Murray Hill Events
            </Link>
            <button
              className="navbar-toggler"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#nav"
              aria-controls="nav"
              aria-expanded="false"
              aria-label="Toggle navigation"
            >
              <span className="navbar-toggler-icon"></span>
            </button>
            <div className="collapse navbar-collapse" id="nav">
              <ul className="navbar-nav me-auto">
                <SectionDropdown prefix="/events" label="Events" icon="calendar-event" items={EVENTS_NAV} />
                <SectionDropdown prefix="/venues" label="Venues" icon="geo-alt" items={VENUES_NAV} />
                <SectionDropdown prefix="/series" label="Series" icon="collection" items={SERIES_NAV} />
                <li className="nav-item">
                  <TopNavLink to="/notifications" label="Notifications" icon="bell" />
                </li>
                <SectionDropdown prefix="/submit" label="Submit" icon="plus-circle" items={SUBMIT_NAV} />
                {isAdmin && (
                  <li className="nav-item">
                    <TopNavLink to="/admin" label="Admin" icon="gear" />
                  </li>
                )}
              </ul>
              <ul className="navbar-nav align-items-lg-center">
                <li className="nav-item"><ThemeToggle /></li>
                <li className="nav-item">
                  <AuthenticatedTemplate><SignOutButton /></AuthenticatedTemplate>
                  <UnauthenticatedTemplate><SignInButton /></UnauthenticatedTemplate>
                </li>
              </ul>
            </div>
          </div>
        </nav>
      </header>

      <main id="main" tabIndex={-1} className="container py-4">
        <Routes>
          {/* Default landing → Events list */}
          <Route path="/" element={<Navigate replace to="/events" />} />

          {/* Events section */}
          <Route path="/events" element={<RequireAuth><HomePage /></RequireAuth>} />
          <Route path="/events/email/:partitionKey/:eventId" element={<RequireAuth><EmailEventPage /></RequireAuth>} />
          <Route path="/events/calendar" element={<RequireAuth><HomePage /></RequireAuth>} />
          <Route path="/events/map" element={<RequireAuth><EventsMapPage /></RequireAuth>} />
          <Route path="/events/calendar-posts" element={<RequireAuth><CalendarPostsPage /></RequireAuth>} />
          <Route path="/events/on-view" element={<RequireAuth><OnViewPage /></RequireAuth>} />
          <Route path="/events/my-taste" element={<RequireAuth><MyTastePage /></RequireAuth>} />
          <Route path="/events/queue" element={<RequireAdmin><AdminQueuePage /></RequireAdmin>} />
          <Route path="/events/manage" element={<RequireAdmin><AdminEventsPage /></RequireAdmin>} />

          {/* Submit section */}
          <Route path="/submit" element={<RequireAuth><AdminSubmitPage /></RequireAuth>} />
          <Route path="/submit/mine" element={<RequireAuth><MySubmissionsPage /></RequireAuth>} />
          <Route path="/submit/history" element={<RequireAdmin><AdminSubmissionsPage /></RequireAdmin>} />
          <Route path="/submit/request-change" element={<RequireAdmin><AdminChangeRequestPage /></RequireAdmin>} />
          <Route path="/events/submit" element={<Navigate replace to="/submit" />} />

          {/* Venues section */}
          <Route path="/venues" element={<RequireAuth><VenuesPublicPage /></RequireAuth>} />
          <Route path="/venues/map" element={<RequireAuth><VenueMapPage /></RequireAuth>} />
          <Route path="/venues/manage" element={<RequireAdmin><AdminVenuesPage /></RequireAdmin>} />
          <Route path="/venues/pending" element={<RequireAdmin><AdminPendingVenuesPage /></RequireAdmin>} />
          <Route path="/venues/:venueId" element={<RequireAuth><VenueDetailPage /></RequireAuth>} />

          {/* Series section */}
          <Route path="/series" element={<RequireAuth><SeriesPublicPage /></RequireAuth>} />
          <Route path="/series/map" element={<RequireAuth><SeriesMapPage /></RequireAuth>} />
          <Route path="/series/manage" element={<RequireAdmin><AdminSeriesPage /></RequireAdmin>} />
          <Route path="/series/:seriesId" element={<RequireAuth><SeriesDetailPage /></RequireAuth>} />

          {/* Notifications hub */}
          <Route path="/notifications" element={<RequireAuth><NotificationsPage /></RequireAuth>} />

          {/* Admin back-office. Sidebar-driven layout (see components/AdminLayout). */}
          <Route path="/admin" element={<Navigate replace to="/admin/scans" />} />
          <Route path="/admin/scans" element={<RequireAdmin><AdminScansPage /></RequireAdmin>} />
          <Route path="/admin/raw-posts" element={<RequireAdmin><AdminIngestionsPage /></RequireAdmin>} />
          <Route path="/admin/venue-identifications" element={<RequireAdmin><AdminIdentifyHistoryPage /></RequireAdmin>} />
          <Route path="/admin/date-recalculations" element={<RequireAdmin><AdminRecalculationsPage /></RequireAdmin>} />
          <Route path="/admin/flagged-for-ai-review" element={<RequireAdmin><AdminAiTrainingPage /></RequireAdmin>} />
          <Route path="/admin/source-feeds" element={<RequireAdmin><AdminSourcesPage /></RequireAdmin>} />
          <Route path="/admin/scan-cadence" element={<RequireAdmin><AdminScanCadencePage /></RequireAdmin>} />
          <Route path="/admin/coverage" element={<RequireAdmin><AdminCoveragePage /></RequireAdmin>} />
          <Route path="/admin/ai-extraction" element={<RequireAdmin><AdminAiExtractionPage /></RequireAdmin>} />
          <Route path="/admin/secrets" element={<RequireAdmin><AdminSecretsPage /></RequireAdmin>} />
          <Route path="/admin/extension-keys" element={<RequireAdmin><AdminExtensionKeysPage /></RequireAdmin>} />
          <Route path="/admin/settings" element={<RequireAdmin><AdminSettingsPage /></RequireAdmin>} />
          <Route path="/admin/notifications" element={<RequireAdmin><AdminNotificationsPage /></RequireAdmin>} />
          <Route path="/admin/jobs" element={<RequireAdmin><AdminJobsPage /></RequireAdmin>} />
          <Route path="/admin/bulk-recalculate" element={<RequireAdmin><AdminBulkRecalcPage /></RequireAdmin>} />
          <Route path="/admin/reports" element={<RequireAdmin><AdminReportsPage /></RequireAdmin>} />

          {/* Legacy /ops/* redirects — preserve existing bookmarks/links. */}
          <Route path="/ops" element={<Navigate replace to="/admin/scans" />} />
          <Route path="/ops/scans" element={<Navigate replace to="/admin/scans" />} />
          <Route path="/ops/scan-cadence" element={<Navigate replace to="/admin/scan-cadence" />} />
          <Route path="/ops/submissions" element={<Navigate replace to="/submit/history" />} />
          <Route path="/ops/ingestions" element={<Navigate replace to="/admin/raw-posts" />} />
          <Route path="/ops/sources" element={<Navigate replace to="/admin/source-feeds" />} />
          <Route path="/ops/change-requests" element={<Navigate replace to="/submit/request-change" />} />
          <Route path="/ops/polygons" element={<Navigate replace to="/admin/coverage" />} />
          <Route path="/ops/identify-history" element={<Navigate replace to="/admin/venue-identifications" />} />
          <Route path="/ops/recalculations" element={<Navigate replace to="/admin/date-recalculations" />} />
          <Route path="/ops/reports" element={<Navigate replace to="/admin/reports" />} />
          <Route path="/ops/jobs" element={<Navigate replace to="/admin/jobs" />} />
          <Route path="/ops/ai-training" element={<Navigate replace to="/admin/flagged-for-ai-review" />} />
          <Route path="/ops/settings" element={<Navigate replace to="/admin/settings" />} />

          {/* Legacy /admin/* redirects (pre-rename URLs). */}
          <Route path="/admin/queue" element={<Navigate replace to="/events/queue" />} />
          <Route path="/admin/events" element={<Navigate replace to="/events/manage" />} />
          <Route path="/admin/submit" element={<Navigate replace to="/submit" />} />
          <Route path="/admin/venues" element={<Navigate replace to="/venues/manage" />} />
          <Route path="/admin/series" element={<Navigate replace to="/series/manage" />} />
          <Route path="/admin/ingestions" element={<Navigate replace to="/admin/raw-posts" />} />
          <Route path="/admin/sources" element={<Navigate replace to="/admin/source-feeds" />} />
          <Route path="/admin/change-request" element={<Navigate replace to="/submit/request-change" />} />
          <Route path="/admin/polygons" element={<Navigate replace to="/admin/coverage" />} />
        </Routes>
      </main>

      <footer className="bg-body-tertiary py-4 mt-5">
        <div className="container small text-body-secondary">
          <p className="mb-0">Murray Hill, Riverside &amp; Avondale events &middot; Jacksonville FL</p>
        </div>
      </footer>
      </RequireAuth>
    </>
  );
}
